import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Sparkles, Package, Boxes, ExternalLink } from "lucide-react";

export default function Dashboard() {
    const { user } = useAuth();
    const firstName = (user?.name || "").split(" ")[0] || user?.email?.split("@")[0] || "";

    return (
        <div className="min-h-screen bg-white">
            <AppHeader />
            <main className="max-w-[1400px] mx-auto px-6 py-10 lg:py-14">
                {/* Hero */}
                <div className="grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6 items-end mb-10 lg:mb-14">
                    <div>
                        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-4 flex items-center gap-2">
                            <Sparkles className="h-3.5 w-3.5 text-[#F59E0B]" />
                            Panel principal
                        </div>
                        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                            Hola{firstName ? `, ${firstName}` : ""}.
                            <br />
                            <span className="text-[#1BA5D9]">Elegí qué cotizar hoy.</span>
                        </h1>
                        <p className="mt-5 text-slate-600 max-w-xl leading-relaxed">
                            Dos herramientas especializadas para MD Link. Configurá clientes, equipos y consumibles con precios, IVA y financiamiento calculados en tiempo real.
                        </p>
                    </div>

                    <div className="border border-slate-200 rounded-md p-5 bg-slate-50/70">
                        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-3">
                            Sesión
                        </div>
                        <div className="text-sm text-slate-900 font-medium truncate" data-testid="dashboard-user-name">
                            {user?.name || "—"}
                        </div>
                        <div className="text-xs text-slate-500 font-mono truncate" data-testid="dashboard-user-email">
                            {user?.email}
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between text-xs">
                            <span className="text-slate-500 font-mono uppercase tracking-widest">Rol</span>
                            <span className="text-slate-900 font-medium">{user?.role || "user"}</span>
                        </div>
                    </div>
                </div>

                {/* Bento cards */}
                <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
                    {/* Unidosis */}
                    <Link
                        data-testid="unidosis-select-card"
                        to="/unidosis"
                        className="group relative overflow-hidden rounded-md border border-slate-200 bg-white hover:border-[#1BA5D9] transition-all duration-150"
                    >
                        <div className="relative h-64 overflow-hidden">
                            <div
                                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                                style={{
                                    backgroundImage:
                                        "url(https://images.unsplash.com/photo-1602052577122-f73b9710adba?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80)",
                                }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/50 to-transparent" />
                            <div className="absolute top-4 left-4 flex items-center gap-2">
                                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm bg-[#F59E0B] text-slate-900 font-semibold">
                                    Disponible
                                </span>
                                <span className="text-[10px] font-mono uppercase tracking-widest text-white/80">
                                    Opus Pac
                                </span>
                            </div>
                            <div className="absolute bottom-4 left-4 right-4 text-white">
                                <div className="flex items-center gap-2 mb-2">
                                    <Package className="h-4 w-4" />
                                    <span className="text-[11px] font-mono uppercase tracking-widest text-white/80">
                                        Cotizador 02
                                    </span>
                                </div>
                                <h3 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
                                    Equipos de Unidosis
                                </h3>
                            </div>
                        </div>
                        <div className="p-5">
                            <p className="text-sm text-slate-600 leading-relaxed">
                                Cotizá equipos Opus Pac (Corina, Nina, MK5, Flag, PIK, IB-Fill) con matriz de consumibles, financiamiento, alquiler y comodato.
                            </p>
                            <div className="mt-4 flex items-center justify-between">
                                <div className="flex gap-4 text-[11px] font-mono uppercase tracking-widest text-slate-500">
                                    <span>4 modos</span>
                                    <span>·</span>
                                    <span>4 familias</span>
                                    <span>·</span>
                                    <span>35+ ref.</span>
                                </div>
                                <div className="flex items-center gap-1 text-[#1BA5D9] font-medium text-sm group-hover:gap-2 transition-all duration-150">
                                    Abrir <ArrowRight className="h-4 w-4" />
                                </div>
                            </div>
                        </div>
                    </Link>

                    {/* Anaqueles → externo */}
                    <a
                        data-testid="anaqueles-select-card"
                        href="https://etiqueta-cotizador.emergent.host"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative overflow-hidden rounded-md border border-slate-200 bg-white hover:border-[#1BA5D9] transition-all duration-150"
                    >
                        <div className="relative h-64 overflow-hidden">
                            <div
                                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                                style={{
                                    backgroundImage:
                                        "url(https://images.unsplash.com/photo-1580281657529-557a6abb6387?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80)",
                                }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/50 to-transparent" />
                            <div className="absolute top-4 left-4 flex items-center gap-2">
                                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm bg-[#F59E0B] text-slate-900 font-semibold">
                                    Disponible
                                </span>
                                <span className="text-[10px] font-mono uppercase tracking-widest text-white/80">
                                    Módulo externo
                                </span>
                            </div>
                            <div className="absolute bottom-4 left-4 right-4 text-white">
                                <div className="flex items-center gap-2 mb-2">
                                    <Boxes className="h-4 w-4" />
                                    <span className="text-[11px] font-mono uppercase tracking-widest text-white/80">
                                        Cotizador 01
                                    </span>
                                </div>
                                <h3 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
                                    Anaqueles
                                </h3>
                            </div>
                        </div>
                        <div className="p-5">
                            <p className="text-sm text-slate-600 leading-relaxed">
                                Cotizador de anaqueles y etiquetas para farmacia hospitalaria. Se abre en una nueva pestaña.
                            </p>
                            <div className="mt-4 flex items-center justify-between">
                                <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 truncate">
                                    etiqueta-cotizador.emergent.host
                                </div>
                                <div className="flex items-center gap-1 text-[#1BA5D9] font-medium text-sm group-hover:gap-2 transition-all duration-150 whitespace-nowrap ml-3">
                                    Abrir <ExternalLink className="h-4 w-4" />
                                </div>
                            </div>
                        </div>
                    </a>
                </div>

                <div className="mt-12 pt-8 border-t border-slate-200 text-xs font-mono uppercase tracking-widest text-slate-400 flex flex-wrap items-center gap-x-6 gap-y-2">
                    <span>MD Link · mdlink.tech</span>
                    <span>Distribuidor autorizado Opus Pac · Costa Rica</span>
                    <span className="ml-auto">Precios en USD · IVA 13%</span>
                </div>
            </main>
        </div>
    );
}
